// I01/I03/I05/I07 — industry calculators & simulators.
import { useMemo, useState } from "react";
import { dealerAdvocacy, basketAnalysis, enrolmentFunnel, selloutPlan, inr } from "./engines";
import { Field, ResultRow, ToolSection, Note, useNumbers } from "./ui";

/* ---------- I01 Dealer Profit & Advocacy Simulator ---------- */
const I01_OURS = { price: 20000, marginPct: 12, incentivePerUnit: 300, monthlyUnits: 10, creditDays: 15 };
const I01_RIVAL = { price: 20000, marginPct: 10, incentivePerUnit: 0, monthlyUnits: 18, creditDays: 30 };
const I01_FIELDS = [["price", "Dealer selling price (₹)"], ["marginPct", "Dealer margin %"], ["incentivePerUnit", "Incentive per unit (₹)"], ["monthlyUnits", "Units the dealer moves / month"], ["creditDays", "Credit days this brand gives the dealer"]];
export function ToolI01() {
  const [ours, setOurs] = useState(I01_OURS);
  const [rival, setRival] = useState(I01_RIVAL);
  const r = useMemo(() => dealerAdvocacy({ ours, rival }), [ours, rival]);
  const col = (vals, setter, label, keyPrefix) => (
    <div className="border border-zinc-200 p-4">
      <div className="text-sm font-medium mb-3">{label}</div>
      <div className="space-y-3">
        {I01_FIELDS.map(([k, fl]) => (
          <Field key={k} label={fl} value={vals[k]} onChange={(v) => setter((s) => ({ ...s, [k]: v }))} testid={`i01-${keyPrefix}-${k}`} />
        ))}
      </div>
    </div>
  );
  const verdicts = {
    winning: ["Winning — dealers earn clearly more pushing you", "good"],
    competitive: ["Competitive — at par, so relationships decide", undefined],
    losing: ["Losing — the rival pays the dealer better", "bad"],
  };
  const [vt, tone] = verdicts[r.verdict];
  return (
    <div data-testid="tool-I01">
      <p className="text-sm text-zinc-600 mb-5">Why should a dealer push your TV over the rival's? Run their math, not yours — per-unit earnings, monthly totals, and the credit cost that quietly eats margin.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {col(ours, setOurs, "Your brand", "ours")}
        {col(rival, setRival, "The rival", "rival")}
      </div>
      <div className="mt-6 max-w-lg" data-testid="i01-result">
        <ResultRow label="Dealer earns per unit — you" value={inr(r.ours.perUnit)} />
        <ResultRow label="Dealer earns per unit — rival" value={inr(r.rival.perUnit)} />
        <ResultRow label="Dealer net per month — you" value={inr(r.ours.netMonthly)} />
        <ResultRow label="Dealer net per month — rival" value={inr(r.rival.netMonthly)} />
        <ResultRow label="Verdict" value={vt} strong tone={tone === "bad" ? "bad" : tone === "good" ? "good" : undefined} />
      </div>
      <ToolSection title="Advocacy interventions, cheapest first">
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
          <li>Faster claims and service resolution — dealers price your friction into their push.</li>
          <li>Demo units and salesperson training — the dealer's floor staff sell what they understand.</li>
          <li>Targeted per-unit spiffs on priority models — cheaper than raising base margin.</li>
          <li>Base margin moves last: permanent, network-wide, and instantly matched by rivals.</li>
        </ul>
      </ToolSection>
    </div>
  );
}

/* ---------- I03 Market Basket Analyzer (CSV) ---------- */
const I03_DEMO = `order_id,item
9001,Rice 5kg
9001,Toor Dal 1kg
9002,Rice 5kg
9002,Toor Dal 1kg
9003,Rice 5kg
9003,Sunflower Oil 1L
9004,Atta 5kg
9004,Sunflower Oil 1L
9005,Rice 5kg
9005,Toor Dal 1kg
9005,Atta 5kg
9006,Atta 5kg
9006,Sunflower Oil 1L
9007,Rice 5kg
9007,Atta 5kg
9008,Atta 5kg
9008,Sunflower Oil 1L
9009,Rice 5kg
9009,Toor Dal 1kg
9010,Sunflower Oil 1L`;
function parseCsvLines(text) {
  const lines = text.trim().split(/\r?\n/);
  const splitRow = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === "," && !inQ) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  return { headers: splitRow(lines[0]), rows: lines.slice(1).map(splitRow) };
}
export function ToolI03() {
  const [parsed, setParsed] = useState(null);
  const [map, setMap] = useState({ order: "", item: "" });
  const [fileName, setFileName] = useState("");
  const [minSupport, setMinSupport] = useState(2);
  const result = useMemo(() => {
    if (!parsed || !map.order || !map.item) return null;
    const oi = parsed.headers.indexOf(map.order);
    const ii = parsed.headers.indexOf(map.item);
    return basketAnalysis(parsed.rows.filter((r) => r.length > 1).map((r) => ({ orderId: r[oi], item: r[ii] })), { minSupportPct: Number(minSupport) || 2 });
  }, [parsed, map, minSupport]);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const p = parseCsvLines(String(reader.result));
      setParsed(p);
      const guess = (cands) => p.headers.find((h) => cands.some((c) => h.toLowerCase().includes(c))) || "";
      setMap({ order: guess(["order", "bill", "invoice", "transaction"]), item: guess(["item", "product", "sku"]) });
    };
    reader.readAsText(f);
  };
  const loadDemo = () => {
    const p = parseCsvLines(I03_DEMO);
    setParsed(p); setFileName("demo-baskets.csv");
    setMap({ order: "order_id", item: "item" });
  };
  return (
    <div data-testid="tool-I03">
      <div className="border border-zinc-200 p-5" data-testid="i03-upload">
        <div className="text-sm font-medium">Upload order lines CSV</div>
        <p className="text-xs text-zinc-500 mt-1">One row per order line: an order/bill ID column and an item column. Parsed locally in your browser — nothing is uploaded anywhere.</p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="i03-file" />
          <button type="button" onClick={loadDemo} className="text-sm font-medium underline underline-offset-4" data-testid="i03-demo">Load demo data (10 baskets)</button>
          <label className="flex items-center gap-2 text-xs text-zinc-600">Min support %
            <input type="number" value={minSupport} onChange={(e) => setMinSupport(e.target.value)} className="w-16 border border-zinc-300 px-1.5 py-1 text-sm" data-testid="i03-minsupport" />
          </label>
        </div>
        {parsed && (
          <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="i03-mapper">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">Order ID column</span>
              <select value={map.order} onChange={(e) => setMap((m) => ({ ...m, order: e.target.value }))} className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="i03-map-order">
                <option value="">Choose…</option>
                {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-widest text-zinc-500">Item column</span>
              <select value={map.item} onChange={(e) => setMap((m) => ({ ...m, item: e.target.value }))} className="mt-1 w-full border border-zinc-300 p-2 text-sm bg-white" data-testid="i03-map-item">
                <option value="">Choose…</option>
                {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
          </div>
        )}
        {fileName && <p className="mt-2 text-xs text-zinc-400">Loaded: {fileName} ({parsed?.rows.length || 0} rows)</p>}
      </div>
      {result && result.pairs.length > 0 && (
        <div className="mt-8" data-testid="i03-result">
          <ResultRow label="Baskets analysed" value={String(result.baskets)} />
          <ResultRow label="Unique items" value={String(result.uniqueItems)} />
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="i03-pairs">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-black">
                  {["Pair", "Together in", "Support", "Confidence", "Lift", "Verdict"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.pairs.map((p, i) => (
                  <tr key={i} className="border-b border-black/10" data-testid={`i03-pair-${i}`}>
                    <td className="py-2 pr-3 font-medium">{p.a} + {p.b}</td>
                    <td className="py-2 pr-3 tabular-nums">{p.count} {p.count === 1 ? "basket" : "baskets"}</td>
                    <td className="py-2 pr-3 tabular-nums">{p.supportPct}%</td>
                    <td className="py-2 pr-3 tabular-nums">{p.confidencePct}%</td>
                    <td className="py-2 pr-3 tabular-nums">{p.lift}×</td>
                    <td className={`py-2 text-xs font-medium ${p.lift >= 1.3 ? "text-emerald-700" : p.lift < 0.9 ? "text-red-700" : "text-zinc-500"}`}>
                      {p.lift >= 1.3 ? "Real association — test a display" : p.lift < 0.9 ? "Anti-correlated — don't pair" : "Weak"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Note>Lift above ~1.3 means the pair genuinely travels together (not just both popular). Sample caveat: under ~500 baskets, lift is noisy — treat findings as display tests to run, not truths. Test: co-locate the pair for two weeks, measure attach-rate change.</Note>
        </div>
      )}
      {result && result.error === "need-more-baskets" && <Note tone="warn">Need at least 2 baskets with the mapped columns. Check the column mapping.</Note>}
      {result && !result.error && result.pairs.length === 0 && <Note tone="warn">No pairs above the minimum support. Lower the threshold or upload more orders.</Note>}
    </div>
  );
}

/* ---------- I05 Enrolment Economics Simulator ---------- */
export function ToolI05() {
  const [v, set] = useNumbers({ monthlyEnquiries: 1000, counsellingPct: 40, applicationPct: 50, enrolmentPct: 30, avgFee: 60000, marketingSpendMonthly: 800000, counsellorMonthlyCost: 200000 });
  const r = useMemo(() => enrolmentFunnel(v), [v]);
  return (
    <div data-testid="tool-I05" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Enquiries / month" value={v.monthlyEnquiries} onChange={set("monthlyEnquiries")} testid="i05-enq" />
          <Field label="Average fee (₹)" value={v.avgFee} onChange={set("avgFee")} testid="i05-fee" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="→ Counselling %" value={v.counsellingPct} onChange={set("counsellingPct")} testid="i05-couns" />
          <Field label="→ Application %" value={v.applicationPct} onChange={set("applicationPct")} testid="i05-app" />
          <Field label="→ Enrolment %" value={v.enrolmentPct} onChange={set("enrolmentPct")} testid="i05-enrol" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Marketing spend / month (₹)" value={v.marketingSpendMonthly} onChange={set("marketingSpendMonthly")} testid="i05-mkt" />
          <Field label="Counsellor cost / month (₹)" value={v.counsellorMonthlyCost} onChange={set("counsellorMonthlyCost")} testid="i05-cost" />
        </div>
      </div>
      <div>
        <div data-testid="i05-result">
          <ResultRow label="Counselled" value={r.counselled.toLocaleString("en-IN")} />
          <ResultRow label="Applications" value={r.applied.toLocaleString("en-IN")} />
          <ResultRow label="Paid enrolments" value={r.enrolled.toLocaleString("en-IN")} strong />
          <ResultRow label="End-to-end conversion" value={`${r.endToEndPct}%`} />
          <ResultRow label="CAC per paid enrolment" value={r.cacPerEnrolment === null ? "—" : inr(r.cacPerEnrolment)} tone="bad" />
          <ResultRow label="Fee revenue / month" value={inr(r.revenue)} />
          <ResultRow label="Weakest stage" value={r.weakestStage} />
          <ResultRow label="Funnel ROI" value={r.roiPct === null ? "—" : `${r.roiPct}%`} strong tone={r.roiPct > 0 ? "good" : "bad"} />
        </div>
        <Note tone="warn">Enquiries are not demand — paid, collected fees are. If ROI is negative, fix the weakest stage before raising spend: a funnel converting 12% end-to-end beats doubling enquiries into 6%.</Note>
      </div>
    </div>
  );
}

/* ---------- I07 Real Estate Sell-Out Planner ---------- */
export function ToolI07() {
  const [v, set] = useNumbers({ totalUnits: 120, soldUnits: 45, avgPrice: 8000000, monthsLeft: 10, siteVisitToBookingPct: 8, leadToVisitPct: 25 });
  const r = useMemo(() => selloutPlan(v), [v]);
  return (
    <div data-testid="tool-I07" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Total units" value={v.totalUnits} onChange={set("totalUnits")} testid="i07-total" />
          <Field label="Sold so far" value={v.soldUnits} onChange={set("soldUnits")} testid="i07-sold" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Average unit price (₹)" value={v.avgPrice} onChange={set("avgPrice")} testid="i07-price" />
          <Field label="Months to target sell-out" value={v.monthsLeft} onChange={set("monthsLeft")} testid="i07-months" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Site visit → booking %" value={v.siteVisitToBookingPct} onChange={set("siteVisitToBookingPct")} testid="i07-s2b" />
          <Field label="Lead → site visit %" value={v.leadToVisitPct} onChange={set("leadToVisitPct")} testid="i07-l2v" />
        </div>
      </div>
      <div>
        <div data-testid="i07-result">
          <ResultRow label="Units to sell" value={String(r.remainingUnits)} />
          <ResultRow label="Bookings needed / month" value={String(r.bookingsPerMonth)} strong />
          <ResultRow label="Site visits needed / month" value={String(r.visitsPerMonth)} />
          <ResultRow label="Qualified leads needed / month" value={String(r.leadsPerMonth)} />
          <ResultRow label="That's per week" value={`${r.weeklyVisits} site visits`} />
          <ResultRow label="Pipeline value at stake" value={inr(r.pipelineValue)} strong />
        </div>
        <ToolSection title="Conversion thresholds to defend">
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
            <li>Below 5% visit→booking: the problem is the site experience or pricing — more leads won't fix it.</li>
            <li>Below 15% lead→visit: lead quality or follow-up speed — audit source mix before scaling spend.</li>
            <li>Two consecutive months behind pace = trigger the mid-course correction (price, payment plan, or channel), not the third month.</li>
          </ul>
        </ToolSection>
      </div>
    </div>
  );
}
